/**
 * PI Loop Constants Calculator — MODO (ps-centric) Phase-Locked Loop.
 *
 * TypeScript port of `pi_loop_constants` (Python reference).
 *
 * The PI loop equation implemented downstream is:
 *   u[n] = Kp * x[n] + I[n]            (control output, DAC units)
 *   I[n] = I[n-1] + Ki * x[n]          (integrator update)
 *
 * with x[n] = (pre-filtered) phase/time-tag offset [ps], u[n] = DAC word [LSB].
 *
 * Hardware model:
 *   KVCO = (Y_step / Fo) * 1e12        [ps / (s * LSB)]
 *   K    = KDET * KVCO                 [1/s]
 *   tau_1 = tau_n^2 * K                integrator time constant [s]
 *   |Kp| = 2*zeta * KDET / sqrt(K * tau_1)
 *   |Ki| = KDET / tau_1
 *
 * Firmware ingests positive magnitudes (the negative sign is applied internally
 * by the controller). The Pydantic schema enforces ge=0 on pi_kp/pi_ki, so we
 * always return absolute values here.
 *
 * Reference cross-check (Excel column G, MODO - TAU, tau_n=60s = 1 minute):
 *   |Kp| ≈ 4.994805e-2
 *   |Ki| ≈ 4.162338e-4
 */

export interface PILoopInputs {
  tau_n_minutes: number;
  zeta: number;
  tau3_ratio: number;
  fo_hz: number;
  y_step_hz: number;
}

export interface PILoopGains {
  kp: number;
  ki: number;
}

export const KDET = 1.0;

export function calculatePIGains(inputs: PILoopInputs): PILoopGains {
  const tau_n = inputs.tau_n_minutes * 60;
  const KVCO = (inputs.y_step_hz / inputs.fo_hz) * 1e12;
  const K = KDET * KVCO;
  const tau_1 = tau_n * tau_n * K;
  const Ap = (2 * inputs.zeta) / Math.sqrt(K * tau_1);
  return {
    kp: Math.abs(Ap * KDET),
    ki: Math.abs(KDET / tau_1),
  };
}
